import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import Ingredients from "@/pages/Ingredients";
import Recipes from "@/pages/Recipes";
import RecipeEditor from "@/pages/RecipeEditor";
import Alerts from "@/pages/Alerts";
import Copilot from "@/pages/Copilot";
import Settings from "@/pages/Settings";
import AdminUsers from "@/pages/AdminUsers";
import AdminVisitors from "@/pages/AdminVisitors";
import { VisitTracker } from "@/components/VisitTracker";
import Landing from "@/pages/Landing";
import MenuAnalyzer from "@/pages/MenuAnalyzer";
import MyMenu from "@/pages/MyMenu";
import MenuAnalysisDetail from "@/pages/MenuAnalysisDetail";
import { postAuthPath } from "@/lib/menuFunnel";
import SeoManager from "@/components/SeoManager";
import Pricing from "@/pages/Pricing";
import PaymentResult from "@/pages/PaymentResult";
import { QuotaDialog } from "@/components/billing/QuotaDialog";

const Loader = () => (
  <div className="flex h-screen items-center justify-center" data-testid="app-loader">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to={postAuthPath()} replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <SeoManager />
          <VisitTracker />
          <QuotaDialog />
          <Routes>
            <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/analyser-ma-carte" element={<MenuAnalyzer />} />
            <Route path="/tarifs" element={<Pricing />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<Protected><AppLayout /></Protected>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/ma-carte" element={<MyMenu />} />
              <Route path="/ma-carte/:id" element={<MenuAnalysisDetail />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/ingredients" element={<Ingredients />} />
              <Route path="/recipes" element={<Recipes />} />
              <Route path="/recipes/new" element={<RecipeEditor />} />
              <Route path="/recipes/:id" element={<RecipeEditor />} />
              <Route path="/alerts" element={<Alerts />} />
              <Route path="/copilot" element={<Copilot />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/payment/success" element={<PaymentResult />} />
              <Route path="/payment/cancel" element={<PaymentResult />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/visitors" element={<AdminVisitors />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position={window.matchMedia("(max-width: 1023px)").matches ? "top-center" : "top-right"} />
      </AuthProvider>
    </ThemeProvider>
  );
}
